import type { DOMEvent } from '../types/html-event-target'
import type { Nullable } from '../types/nullable'
import toggleClass from './helpers/toggleClass'

/**
 * Adds functionality for the dropdown menus on TMS banner.
 * Works with elements of class 'main-nav-toggle'.
 * Use on documentReady
 *
 */
const navBar = (): void => {
  const html: HTMLElement = document.querySelector('html')
  const toggles: HTMLElement[] = Array.from(document.querySelectorAll('.main-nav-toggle'))
  const activeClassName = 'active'

  /**
   * Closes all toggles, except the toggle passed in (optional)
   * @param {HTMLElement?} except The toggle(Element) that should not be closed.
   * @example ```
   *    // All main nav toggle elements that have lists will close, bar myToggle
   *    closeAllToggles(myToggle)
   * ```
   * @example ```
   *    // All main nav toggle elements will close.
   *    closeAllToggles()
   * ```
   */
  const closeAllToggles = (except: Nullable<HTMLElement> = null): void => {
    toggles.filter((toggle) => (
      toggle !== except
    )).forEach((toggle): void => {
      const next = toggle.nextElementSibling
      toggle.classList.remove(activeClassName)
      next.classList.remove(activeClassName)
      next.setAttribute('aria-hidden', 'true')
    })
  }

  /**
   * Toggles an aria attribute for a given element.
   * @param {HTMLElement} element The element you wish to toggle the attribute on
   * @param {string} attribute The suffix of the aria attribute.
   *  Ie if you wish to toggle aria-hidden, it would just be 'hidden'
   * @example toggleAttribute(myEl, 'hidden');
   */
  const toggleAriaAttribute = (element: HTMLElement, attribute = 'hidden'): void => {
    const fullAttribute = `aria-${attribute}`
    const value = element.getAttribute(fullAttribute)

    if (value === 'true') {
      element.setAttribute(fullAttribute, 'false')
    } else {
      element.setAttribute(fullAttribute, 'true')
    }
  }

  html.addEventListener('click', (event: DOMEvent<HTMLElement>) => {
    const { target } = event
    if (target.tagName === 'A') return
    closeAllToggles()
  })

  toggles.forEach((toggle) => {
    toggle.addEventListener('click', (event) => {
      closeAllToggles(toggle)

      toggleClass(toggle, activeClassName)

      const next = toggle.nextElementSibling as HTMLElement
      toggleClass(next, activeClassName)
      toggleAriaAttribute(next)
      event.stopImmediatePropagation()
    })
  })
}

export default navBar
